/**
 * Live availability guard for Nivoda diamonds.
 * Asks the backend whether the stone is still AVAILABLE at Nivoda (bypassing caches). When it is not,
 * the backend retires the Shopify product and this element shows a notice and disables the relevant
 * action (select on the diamond page, add-to-cart on the complete-ring page, checkout in the cart).
 * Network or backend errors fail open so a backend hiccup never blocks a sale of a live stone.
 */
if (!customElements.get('diamond-availability')) {
  class DiamondAvailability extends HTMLElement {
    connectedCallback() {
      const cert = this.dataset.cert;
      if (!cert) return;
      const base = window.diamonds_route || 'https://mouza-nivoda-7d54f737c1b9.herokuapp.com/api';
      const handle = this.dataset.handle || '';
      const url = `${base}/diamonds/availability/${encodeURIComponent(cert)}?handle=${encodeURIComponent(handle)}`;
      fetch(url, { cache: 'no-store' })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && data.available === false) this.markUnavailable();
        })
        .catch(() => {});
    }

    markUnavailable() {
      window.__diamondUnavailable = true;
      DiamondAvailability.ensureStyles();
      const notice = document.createElement('div');
      notice.className = 'diamond-availability__notice';
      notice.setAttribute('role', 'alert');
      notice.textContent = this.dataset.message || 'This diamond is no longer available.';
      const searchUrl = this.dataset.searchUrl || '';
      if (searchUrl.startsWith('/') || searchUrl.startsWith('https://')) {
        const link = document.createElement('a');
        link.className = 'diamond-availability__link';
        link.href = searchUrl;
        link.textContent = 'Browse available diamonds';
        notice.appendChild(link);
      }
      // The answer arrives after the page has loaded, so on the product and complete ring pages the notice floats:
      // in the page it would push everything down, and inside the mobile sticky bar it would be squeezed beside the button.
      if (this.dataset.mode === 'pdp' || this.dataset.mode === 'complete') {
        notice.classList.add('diamond-availability__notice--floating');
        document.body.appendChild(notice);
      } else {
        this.replaceChildren(notice);
      }

      const selectors = {
        pdp: 'diamond-actions button, sticky-atcs .button, .journey-tracker__step-link[data-action="submit-diamond"]',
        complete: 'complete-ring-actions button',
        cart: 'button[name="checkout"], .additional-checkout-buttons',
      };
      const selector = selectors[this.dataset.mode] || '';
      if (!selector) return;
      document.querySelectorAll(selector).forEach((el) => {
        if (el.tagName === 'BUTTON' || el.tagName === 'A') el.setAttribute('disabled', 'disabled');
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('diamond-availability--disabled');
        // Dynamic checkout buttons (Shop Pay, PayPal) are rendered by Shopify inside this container;
        // hide it outright so keyboard focus cannot reach them either.
        if (el.classList.contains('additional-checkout-buttons')) {
          el.style.display = 'none';
          el.setAttribute('inert', '');
        }
      });
    }

    static ensureStyles() {
      if (document.getElementById('diamond-availability-style')) return;
      const style = document.createElement('style');
      style.id = 'diamond-availability-style';
      style.textContent = `
        .diamond-availability__notice { margin: 0 0 12px; padding: 12px 16px; border: 1px solid #c0392b; background: #fdf3f2; color: #7a1f14; font-size: 14px; line-height: 1.4; }
        .diamond-availability__notice--floating { position: fixed; left: 12px; right: 12px; bottom: 96px; z-index: 1000; margin: 0; box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2); }
        @media (min-width: 769px) {
          .diamond-availability__notice--floating { left: auto; right: 24px; max-width: 480px; }
        }
        .diamond-availability__link { color: inherit; text-decoration: underline; margin-left: 4px; }
        .diamond-availability--disabled { opacity: 0.45 !important; pointer-events: none !important; cursor: not-allowed !important; }
      `;
      document.head.appendChild(style);
    }
  }
  customElements.define('diamond-availability', DiamondAvailability);
}
